import React, { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Clock, CreditCard, Mail, Phone, RotateCcw } from "lucide-react";
import { toast } from "sonner";

interface SubscriptionData {
  planName: string;
  planPrice: number;
  planPeriod: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  lastPaymentAttempt?: {
    date: string;
    amount: number;
    status: string;
    reason?: string;
  };
  hoursUntilDeactivation?: number;
}

interface SubscriptionExpiredScreenProps {
  subscriptionData?: SubscriptionData;
  onRenewClick: () => void;
  onUpdatePaymentClick: () => void;
  userName?: string;
}

export function SubscriptionExpiredScreen({
  subscriptionData,
  onRenewClick,
  onUpdatePaymentClick,
  userName = "usuário"
}: SubscriptionExpiredScreenProps) {
  const [isRenewing, setIsRenewing] = useState(false);
  
  // Calcular horas restantes (padrão: 48 horas)
  const hoursRemaining = subscriptionData?.hoursUntilDeactivation || 48;
  const daysRemaining = Math.floor(hoursRemaining / 24);
  const hoursRemainingMod = hoursRemaining % 24;
  
  const handleRenewClick = async () => {
    setIsRenewing(true);
    try {
      await onRenewClick();
    } catch (error) {
      toast.error("Erro ao renovar assinatura");
    } finally {
      setIsRenewing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header com alerta crítico */}
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center animate-pulse">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Sua Assinatura Expirou</h1>
          <p className="text-lg text-gray-600">
            Olá {userName}! Sua assinatura {subscriptionData?.planName} venceu. Renovar é rápido!
          </p>
        </div>

        {/* Alerta de urgência com contador */}
        <Alert className="border-red-300 bg-red-50">
          <Clock className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800 ml-2">
            <strong>⏰ Acesso será desativado em:</strong> {daysRemaining}d {hoursRemainingMod}h
            <br />
            <span className="text-sm mt-1 block">
              Renove agora para manter seu acesso e não perder dados!
            </span>
          </AlertDescription>
        </Alert>

        {/* Motivo da falha (se tiver dados) */}
        {subscriptionData?.lastPaymentAttempt && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-base text-orange-900">
                ❌ Último Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-orange-800">
              <div className="flex justify-between">
                <span>Data:</span>
                <strong>{subscriptionData.lastPaymentAttempt.date}</strong>
              </div>
              <div className="flex justify-between">
                <span>Valor:</span>
                <strong>R$ {subscriptionData.lastPaymentAttempt.amount.toFixed(2).replace('.', ',')}</strong>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <strong>Falhou</strong>
              </div>
              {subscriptionData.lastPaymentAttempt.reason && (
                <div className="flex justify-between border-t border-orange-200 pt-2 mt-2">
                  <span>Motivo:</span>
                  <strong>{subscriptionData.lastPaymentAttempt.reason}</strong>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CTAs Principais */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* CTA 1: Renovar Agora (Destaque) */}
          <Button
            onClick={handleRenewClick}
            disabled={isRenewing}
            className="col-span-1 sm:col-span-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold h-12 text-base"
          >
            {isRenewing ? (
              <>
                <RotateCcw className="w-5 h-5 mr-2 animate-spin" />
                Renovando...
              </>
            ) : (
              <>
                <RotateCcw className="w-5 h-5 mr-2" />
                Renovar Agora - {subscriptionData?.planPrice.toFixed(2).replace('.', ',')} por mês
              </>
            )}
          </Button>

          {/* CTA 2: Atualizar Pagamento */}
          <Button
            onClick={onUpdatePaymentClick}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Atualizar Pagamento
          </Button>

          {/* CTA 3: Falar com Suporte via WhatsApp */}
          <Button
            onClick={() => {
              const phoneNumber = '5561982321534';
              const message = 'Olá! Paguei mas meu acesso continua bloqueado. Preciso de ajuda urgente!';
              const cleanNumber = phoneNumber.replace(/\D/g, '');
              const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
              window.open(whatsappUrl, '_blank');
            }}
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-50"
          >
            <Phone className="w-4 h-4 mr-2" />
            Falar com Suporte
          </Button>
        </div>

        {/* Link de Ajuda Menor */}
        <div className="text-center">
          <button 
            onClick={() => {
              const phoneNumber = '5561982321534';
              const message = 'Olá! Tenho uma dúvida sobre minha assinatura.';
              const cleanNumber = phoneNumber.replace(/\D/g, '');
              const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
              window.open(whatsappUrl, '_blank');
            }}
            className="text-sm text-gray-600 hover:text-green-600 underline hover:no-underline transition-all"
          >
            💬 Dúvida? Clique aqui para conversar
          </button>
        </div>

        {/* Histórico da Assinatura */}
        {subscriptionData && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histórico da Assinatura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-gray-600 text-xs">Plano</div>
                  <div className="font-semibold text-gray-900">{subscriptionData.planName}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-gray-600 text-xs">Valor Mensal</div>
                  <div className="font-semibold text-gray-900">
                    R$ {subscriptionData.planPrice.toFixed(2).replace('.', ',')}
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-gray-600 text-xs">Data de Ativação</div>
                  <div className="font-semibold text-gray-900">{subscriptionData.subscriptionStartDate}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-gray-600 text-xs">Data de Vencimento</div>
                  <div className="font-semibold text-red-600">{subscriptionData.subscriptionEndDate}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Informações sobre renovação */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-base text-blue-900">💡 Como funciona a renovação?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-blue-800">
            <p>
              ✓ Quando você clica em "Renovar Agora", sua assinatura é reativada imediatamente
            </p>
            <p>
              ✓ O acesso volta ao normal em poucos minutos
            </p>
            <p>
              ✓ Seu novo vencimento será 1 mês (ou 1 ano) a partir de hoje
            </p>
            <p>
              ✓ Se o pagamento falhar novamente, entraremos em contato
            </p>
          </CardContent>
        </Card>

        {/* FAQ Rápido */}
        <div className="space-y-2 text-sm">
          <details className="bg-white border border-gray-200 rounded-lg p-4">
            <summary className="font-semibold text-gray-900 cursor-pointer">
              Por que meu pagamento falhou?
            </summary>
            <p className="mt-2 text-gray-700">
              Geralmente por: cartão expirado, saldo insuficiente, limite atingido ou dados incorretos.
              Verifique se os dados do seu cartão ou conta bancária estão atualizados.
            </p>
          </details>

          <details className="bg-white border border-gray-200 rounded-lg p-4">
            <summary className="font-semibold text-gray-900 cursor-pointer">
              Meus dados estão seguros?
            </summary>
            <p className="mt-2 text-gray-700">
              Sim! Todos os seus dados de pedidos, clientes e configurações estão seguros em nossa base.
              O acesso é apenas temporariamente desativado enquanto a assinatura não é renovada.
            </p>
          </details>

          <details className="bg-white border border-gray-200 rounded-lg p-4">
            <summary className="font-semibold text-gray-900 cursor-pointer">
              E se tiver dúvidas sobre o valor?
            </summary>
            <p className="mt-2 text-gray-700">
              Nossa equipe está disponível 24/7 para ajudar. Clique em "Falar com Suporte" ou
              envie um email para support@ateliepro.com.br
            </p>
          </details>
        </div>

        {/* Footer com aviso */}
        <div className="text-center text-xs text-gray-600 pt-4 border-t border-gray-200">
          <p>
            Ao renovar, você concorda com nossos{" "}
            <a href="/termos" className="text-blue-600 hover:underline">
              Termos de Serviço
            </a>
            . Dúvidas?{" "}
            <a href="mailto:support@ateliepro.com.br" className="text-blue-600 hover:underline">
              Contate-nos
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
